<template>
  <div>
    <PriceWrapper
      :is-from-google-shopping
      :mobile-plan
      :price
      :price-due-today
      :product
      :selected-offer
      :with-assurance-fee
    >
      <template #refurbished-price>
        {{
          /* empty template to avoid rendering the refurbished price
        (default content of the slot if nothing is provided) */ ''
        }}
      </template>
      <template #first-row-price v-if="buybackOffer && !hasOriginalPrice">
        {{
          /* empty template to avoid rendering the first row
        (default content of the slot if nothing is provided) */ ''
        }}
      </template>
      <template #first-row-price v-else-if="!buybackOffer || hasOriginalPrice">
        <span
          v-if="!buybackOffer"
          class="heading-2"
          data-qa="productpage-product-price"
          data-test="productpage-product-price"
        >
          {{ i18n.price(price) }}
        </span>
        <PriceMainLabel
          v-if="!buybackOffer"
          :buyback-offer="null"
          :is-from-google-shopping
          :mobile-plan-offer="mobilePlan"
        />
        <OriginalPrice
          v-if="
            buybackOffer && hasOriginalPrice && product.type !== 'Brand new'
          "
          :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
          :price-when-new="product.priceWhenNew"
        />
        <DealTag
          v-if="
            buybackOffer && flashSalesDiscount && isFlashSalesExperimentEnabled
          "
          :icon="IconBoltFilled"
          :label="
            i18n(translations.flashSalesTag, {
              price: i18n.price(flashSalesDiscount),
            })
          "
        />
        <YouSave
          v-else-if="
            buybackOffer &&
            hasOriginalPrice &&
            hasSavings &&
            selectedOffer.price
          "
          :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
          :mobile-plan
          :price="selectedOffer.price"
          :price-when-new="product.priceWhenNew"
        />
      </template>
      <template #second-row-price v-if="buybackOffer">
        <PriceDueTodayLabel :buyback-offer :price-due-today />
      </template>
      <template #third-row-price v-if="buybackOffer">
        <span
          class="heading-2"
          data-qa="productpage-product-price"
          data-test="productpage-product-price"
        >
          {{ i18n.price(price) }}
        </span>
        <PriceMainLabel :buyback-offer />
        <RevTag
          v-if="product.type === 'Brand new'"
          :label="i18n(translations.newTaglabel)"
          variant="info"
        />
      </template>
    </PriceWrapper>
  </div>
</template>

<script lang="ts" setup>
import type { Price } from '@backmarket/http-api'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import DealTag from '@backmarket/nuxt-layer-recommendation/DealTag.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTag } from '@ds/components/Tag'
import { IconBoltFilled } from '@ds/icons/IconBoltFilled'
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep'

import translations from './Price.translations'
import PriceWrapper from './PriceWrapper.vue'
import OriginalPrice from './components/OriginalPrice/OriginalPrice.vue'
import PriceDueTodayLabel from './components/PriceLabel/components/PriceDueTodayLabel.vue'
import PriceMainLabel from './components/PriceLabel/components/PriceMainLabel.vue'
import YouSave from './components/YouSave/YouSave.vue'

withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    price: Price
    priceDueToday?: Price | null
    product: GetProductResponse
    mobilePlan?: ReadonlyObjectDeep<MobilePlanOffer>
    flashSalesDiscount?: Price
    hasSavings?: boolean
    hasOriginalPrice?: boolean
    withAssuranceFee?: boolean
    isFromGoogleShopping?: boolean
  }>(),
  {
    mobilePlan: undefined,
    priceDueToday: undefined,
    flashSalesDiscount: undefined,
    hasSavings: true,
    hasOriginalPrice: true,
    withAssuranceFee: false,
    isFromGoogleShopping: false,
  },
)

const i18n = useI18n()
const { buybackOffer } = useBuybackOffer()

const experiments = useExperiments()

const isFlashSalesExperimentEnabled =
  experiments['experiment.flashSales'] === 'enabled'
</script>
